/**
 * Table cell that renders a pencil icon and opens a {@link StatusLog} sidebar
 * modal for viewing / adding status-log comments on a PA order.
 * @module SideModalIconCell
 */

import { useState } from "react";

import StatusLog from "../../../../pages/worklists/genericAuthStatusLayout/components/statusLog";
import NoteWithPenIcon from "../../../../../shared/svg/noteWithPenIcon";
import { PencilIcon } from "../../../../../shared/svg/pencil-Icon";
import SideModal from "../../../modals/filterModal/filterModal";

/** Props for the {@link SideModalIconCell} table cell component. */
interface SideModalIconCellProps {
  /** Cell value containing the data needed to render the icon and open the sidebar. */
  value: {
    /** PA order ID. */
    id: string;
    /** Current master auth status (determines the log-entry note type). */
    status: string;
    /** Hex colour for the right-border indicator (transparent when unset). */
    borderColor: string;
    /** Whether the order already has status-log notes attached. */
    isNotesPresent: boolean;
    /** Patient ID associated with the order, forwarded to the StatusLog insert. */
    patientId: string;
  };
  /** Optional callback fired after the StatusLog sidebar closes. */
  onSideModalClose?: () => void;
}

/**
 * Renders a pencil/note icon inside a table cell with a coloured right border.
 * Clicking the icon opens a {@link SideModal} containing the {@link StatusLog}
 * sidebar where users can create or review status-log entries for the order.
 *
 * @param props - Cell value and optional side-modal close callback.
 */
export const SideModalIconCell = (props: SideModalIconCellProps) => {
  const { id, status, borderColor, isNotesPresent, patientId } = props.value;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleIconClick = async () => {
    setIsModalOpen(true);
  };

  return (
    <div
      className={`flex h-full w-full items-center justify-center border-r-4 pr-2`}
      onClick={(e) => {
        e.stopPropagation();
        handleIconClick();
      }}
      style={{
        borderColor: borderColor ? borderColor : "transparent",
      }}
    >
      <div className="shrink-0">
        {isNotesPresent ? <NoteWithPenIcon /> : <PencilIcon />}
      </div>
      {isModalOpen && (
        <SideModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          width="35%"
          children={
            <StatusLog
              preSelectedNotesColor={borderColor ?? "#ffffff"}
              onClose={() => {
                setIsModalOpen(false);
              }}
              orderId={id}
              onSideModalClose={props.onSideModalClose}
              status={status}
              patientId={patientId}
            />
          }
        />
      )}
    </div>
  );
};
